import { Upload, X } from "lucide-react";
import { useAuth } from "../../../context/AuthContext";
import { dictionary } from "../../../utils/dictionary";

interface ProductManualInputsProps {
	manualData: any;
	handleImageUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
	handleRemoveImage: (index: number) => void;
	handleManualDataChange: (field: string, value: string) => void;
	isUploadingImages?: boolean;
}

export function ProductManualInputs({
	manualData,
	handleImageUpload,
	handleRemoveImage,
	handleManualDataChange,
	isUploadingImages = false
}: ProductManualInputsProps) {

	const { language } = useAuth();

	return (
		<div className="max-w-4xl mx-auto">
			<div className="grid grid-cols-2 gap-6">

				<label className="block cursor-pointer">
					<div className="bg-[#FCFDFD] transition-all duration-300 border-2 border-dashed border-gray-200 rounded-[35px] p-12 text-center hover:border-[#4F46E5] transition-colors h-full flex flex-col items-center justify-center">

						{isUploadingImages ? (
							<>
								<div className="w-12 h-12 bg-white rounded-full flex items-center justify-center mb-4 shadow-[0_0_15px_rgba(0,0,0,0.2)]">
									<div className="w-6 h-6 border-4 border-gray-200 border-t-[#4F46E5] rounded-full animate-spin" />
								</div>
								<p className="text-base font-semibold text-gray-700 mb-1">
									{dictionary?.["uploading-images"]?.[language] || "Cargando Imagenes..."}
								</p>
							</>
						) : (
							<>
								<div className="w-12 h-12 bg-white rounded-full flex items-center justify-center mb-4 shadow-[0_0_15px_rgba(0,0,0,0.2)]">
									<Upload className="w-6 h-6 text-[#4F46E5]" />
								</div>
								<p className="text-base font-semibold text-gray-700 mb-1">
									{dictionary["upload-photo"][language]}
								</p>
								<p className="text-xs font-semibold text-gray-400">
									{dictionary["accepted-formats"][language]}
								</p>
							</>
						)}
					</div>

					<input
						type="file"
						accept="image/*"
						multiple
						onChange={handleImageUpload}
						disabled={isUploadingImages}
						className="hidden"
					/>
				</label>

				<div className="space-y-4 flex flex-col justify-center">
					<input
						type="text"
						placeholder={dictionary["product-name"][language]}
						value={manualData.name}
						onChange={(e) => handleManualDataChange('name', e.target.value)}
						className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#4F46E5] focus:outline-none text-gray-600 placeholder-gray-400"
					/>

					<textarea
						placeholder={dictionary["product-description"][language]}
						value={manualData.description}
						onChange={(e) => handleManualDataChange('description', e.target.value)}
						className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#4F46E5] focus:outline-none resize-none text-gray-600 placeholder-gray-400"
						rows={3}
					/>
				</div>
			</div>

			{manualData.images.length > 0 && (
				<div className="grid grid-cols-5 gap-3 mt-6">
					{manualData.images.map((img: string, index: number) => (
						<div
							key={index}
							className="relative group rounded-xl overflow-hidden border"
						>
							<img
								src={img}
								alt="Product"
								className="w-full h-20 object-cover"
							/>

							<button
								type="button"
								onClick={(e) => {
									e.preventDefault();
									e.stopPropagation();
									handleRemoveImage(index);
								}}
								className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
							>
								<X className="w-3 h-3" />
							</button>
						</div>
					))}
				</div>
			)}
		</div>
	);
}
